import React, {
  Fragment,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  useEnterpriseGetOne,
  useMainEnterpriseId,
} from "../../../../../../../hooks/enterprise"
import LayoutMain from "../../../../../../../components/Layouts/LayoutMain"
import { useShopGetOne, useShopId } from "../../../../../../../hooks/shop"
import LayoutLoadingAuthRequired from "../../../../../../../components/Layouts/LayoutLoadingAuthRequired"
import HeaderSelectTop from "../../../../../../../components/Headers/HeaderSelectTop"
import Link from "next/link"
import { ArrowLeft } from "lucide-react"
import { useShopSalePointId } from "../../../../../../../hooks/shopSalePoint"
import { useItemFindAll } from "../../../../../../../hooks/item"
import { useRouter } from "next/router"
import { CreateLocalStorageKeyViewedItems } from "../../../../../../../libs/vars/local_storage"
import { Item } from "../../../../../../../libs/api/backend/src"
import RevisionAudio from "../../../../../../../components/Revision/RevisionAudio"
import RevisionHeader from "../../../../../../../components/Revision/RevisionHeader"
import RevisionGridCol from "../../../../../../../components/Revision/RevisionGridCol"

const Title = "Ревизия"

export interface FilterProps {
  searchText?: string
  categoryId?: string
}

interface moveItemProps {
  fromItems: Item[]
  toItems: Item[]
}

function RevisionPage() {
  const enterpriseId = useMainEnterpriseId()
  const shopId = useShopId()
  const shopSalePointId = useShopSalePointId()

  // --------------------------------------------------------
  // ROUTER
  // --------------------------------------------------------

  const router = useRouter()
  const [isReady, setIsReady] = useState(false)

  // save items for revision
  useEffect(() => {
    if (router?.isReady) {
      setIsReady(true)
    }
  }, [router, router.isReady])

  // --------------------------------------------------------
  // AUDIO
  // --------------------------------------------------------

  const audioSuccessRef = useRef<HTMLAudioElement | null>(null)
  const audioFailedRef = useRef<HTMLAudioElement | null>(null)

  const playSound = (audioRef: React.RefObject<HTMLAudioElement | null>) => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current.play()
    } else {
      // Throw error
      console.error("audioRef.current is null")
    }
  }

  const playSuccess = () => {
    playSound(audioSuccessRef)
  }

  const playFailed = () => {
    playSound(audioFailedRef)
  }

  // --------------------------------------------------------
  // HELPERS
  // --------------------------------------------------------

  const getCountItems = (items: Item[]): number => {
    let count = 0
    return (items || []).reduce(
      (accumulator, currentValue) =>
        accumulator + (Number(currentValue.count) || 0),
      count
    )
  }

  const moveItemToAdded = (
    fromItems: Item[],
    toItems: Item[],
    searchText: string
  ): moveItemProps | null => {
    let foundRevisionItem: Item | null = null
    let foundRevisionItemIndex = -1

    let newRevisionItems = [...(fromItems || [])]

    newRevisionItems?.forEach((revisionItem: Item, index: number) => {
      if (!foundRevisionItem && revisionItem.barcode === searchText) {
        foundRevisionItem = revisionItem
        foundRevisionItemIndex = index
      }
    })

    if (!foundRevisionItem) {
      console.error("Item not found with barcode: ", searchText)
      playFailed()
      return null
    }

    let foundRevisionItemCount = Number((foundRevisionItem as Item).count) || 0

    let foundViewedItem: Item | null = null
    let foundViewedItemIndex = -1

    let newViewedItems = [...(toItems || [])]

    newViewedItems?.forEach((viewedItem: Item, index: number) => {
      if (
        !foundViewedItem &&
        viewedItem.id === (foundRevisionItem as Item).id
      ) {
        foundViewedItem = viewedItem
        foundViewedItemIndex = index
      }
    })

    // Add 1 count to viewed item
    let foundViewedItemCount =
      (Number((foundViewedItem as Item | null)?.count) || 0) + 1

    // Add to viewed items or update count
    if (foundViewedItem) {
      newViewedItems[foundViewedItemIndex] = {
        ...newViewedItems[foundViewedItemIndex],
        count: String(foundViewedItemCount),
      }
    } else {
      // Add to start
      newViewedItems = [
        {
          ...(foundRevisionItem as Item),
          count: String(foundViewedItemCount),
        },
        ...newViewedItems,
      ]
    }

    // Remove 1 count from revision item
    foundRevisionItemCount = Math.max(0, foundRevisionItemCount - 1)
    newRevisionItems[foundRevisionItemIndex] = {
      ...newRevisionItems[foundRevisionItemIndex],
      count: String(foundRevisionItemCount),
    }

    // Remove 0 count viewed items
    newViewedItems = newViewedItems.filter((viewedItem: Item) => {
      return (Number(viewedItem.count) || 0) > 0
    })

    // Remove 0 count reviewed items
    newRevisionItems = newRevisionItems.filter((revisionItem: Item) => {
      return (Number(revisionItem.count) || 0) > 0
    })

    return {
      fromItems: newRevisionItems,
      toItems: newViewedItems,
    }
  }

  // --------------------------------------------------------
  // FETCH
  // --------------------------------------------------------

  const { isLoading: isLoadingEnterprise, isError: isErrorEnterprise } =
    useEnterpriseGetOne(enterpriseId)
  const { isLoading: isLoadingShop, isError: isErrorShop } = useShopGetOne(
    enterpriseId,
    shopId
  )

  let {
    isLoading: isLoadingItems,
    isError: isErrorItems,
    items,
  } = useItemFindAll(enterpriseId, {
    shopId,
    limit: -1,
    offset: 0,
  })

  // --------------------------------------------------------
  // REVISION ITEMS
  // --------------------------------------------------------

  const [revisionItems, setRevisionItems] = useState<Item[]>([])
  const [countRevisionItems, setCountRevisionItems] = useState(0)

  // Change setCountRevisionItems
  useEffect(() => {
    setCountRevisionItems(getCountItems(revisionItems))
  }, [revisionItems])

  function convertRevisionItems(items: Item[]) {
    const itemsForRevision: Array<Item> = JSON.parse(JSON.stringify(items))
    return itemsForRevision
  }

  function onlySetRevisionItems(items: Item[]) {
    setRevisionItems(items)
  }

  function convertAndSetRevisionItems(items: Item[]) {
    setRevisionItems(convertRevisionItems(items))
  }

  // --------------------------------------------------------
  // VIEWED ITEMS
  // --------------------------------------------------------

  const [viewedItemsFromStorage, setViewedItemsFromStorage] = useState<Item[]>(
    []
  )
  const [isReadyViewedItemsFromStorage, setIsReadyViewedItemsFromStorage] =
    useState(false)
  const [viewedItems, setViewedItems] = useState<Item[]>([])
  const [countViewedItems, setCountViewedItems] = useState(0)

  // Change setCountViewedItems
  useEffect(() => {
    setCountViewedItems(getCountItems(viewedItems))

    // Save to storage
    localStorage.setItem(
      CreateLocalStorageKeyViewedItems(enterpriseId, shopId),
      JSON.stringify(viewedItems)
    )
  }, [viewedItems])

  // 2. get already reviewed items from storage
  useEffect(() => {
    console.log("useEffect: isReady - get from storage")

    const viewedItemsFromStorage: Array<Item> =
      JSON.parse(
        localStorage.getItem(
          CreateLocalStorageKeyViewedItems(enterpriseId, shopId)
        )
      ) || []

    setViewedItemsFromStorage(viewedItemsFromStorage || [])
    setIsReadyViewedItemsFromStorage(true)
  }, [isReady])

  // 3. filter items for revision with already reviewed items
  useEffect(() => {
    console.log(
      "useEffect: isReadyViewedItemsFromStorage - filter items for tables"
    )

    const revisionItemsTemp = convertRevisionItems(items)

    // Check viewedItems for actual (not sold) items
    const viewedItems = viewedItemsFromStorage.filter(
      (viewedItemFromStorage) => {
        for (const revisionItem of revisionItemsTemp) {
          if (viewedItemFromStorage.id === revisionItem.id) {
            return true
          }
        }
      }
    )

    // filter items for revision with already reviewed items
    let revisionItems = revisionItemsTemp

    if (viewedItems.length > 0) {
      revisionItems = revisionItemsTemp.filter((revisionItem) => {
        for (const viewedItem of viewedItems) {
          if (viewedItem.id !== revisionItem.id) {
            return true
          }
        }
      })
    }

    // Save right table: viewedItems
    setViewedItems(viewedItems || [])
    // Save left table: revisionItems
    onlySetRevisionItems(revisionItems || [])
  }, [isReadyViewedItemsFromStorage])

  // --------------------------------------------------------
  // SEARCH VIEW
  // --------------------------------------------------------

  const [searchText, setSearchText] = useState("")

  // --------------------------------------------------------
  // TABLE VIEW
  // --------------------------------------------------------

  const [limitRevisionItems, setLimitRevisionItems] = useState(10)
  const [offsetRevisionItems, setOffsetRevisionItems] = useState(0)

  const [limitViewedItems, setLimitViewedItems] = useState(10)
  const [offsetViewedItems, setOffsetViewedItems] = useState(0)

  // Filter for find items

  const addItemToViewed = async (filter: FilterProps) => {
    const { searchText } = filter

    // Is this barcode?
    if (searchText.length >= 12) {
      let movedItems = moveItemToAdded(revisionItems, viewedItems, searchText)

      if (!movedItems) {
        return
      }

      setRevisionItems(movedItems.fromItems)
      setViewedItems(movedItems.toItems)

      playSuccess()

      setSearchText("")
    } else {
      setSearchText(searchText)
    }
  }

  // Filter for find items
  const removeItemFromViewed = async (filter: FilterProps) => {
    const { searchText } = filter

    // Is this barcode?
    if (searchText.length >= 12) {
      let movedItems = moveItemToAdded(viewedItems, revisionItems, searchText)

      if (!movedItems) {
        return
      }

      setViewedItems(movedItems.fromItems)
      setRevisionItems(movedItems.toItems)

      playSuccess()

      setSearchText("")
    } else {
      setSearchText(searchText)
    }
  }

  const setLimitOffsetRevisionItems = (limit: number, offset: number) => {
    if (limit < 0 || limit > (revisionItems?.length || 0)) {
      limit = 0
    }
    if (offset < 0 || offset > (revisionItems?.length || 0)) {
      offset = 0
    }
    setLimitRevisionItems(limit)
    setOffsetRevisionItems(offset)
  }

  const setLimitOffsetViewedItems = (limit: number, offset: number) => {
    if (limit < 0 || limit > (viewedItems?.length || 0)) {
      limit = 0
    }
    if (offset < 0 || offset > (viewedItems?.length || 0)) {
      offset = 0
    }
    setLimitViewedItems(limit)
    setOffsetViewedItems(offset)
  }

  const cancelRevision = () => {
    setOffsetRevisionItems(0)
    setOffsetViewedItems(0)

    setSearchText("")

    const itemsForRevision = convertRevisionItems(items)

    setViewedItems([])
    setRevisionItems(itemsForRevision || [])
  }

  const finishRevision = () => {
    cancelRevision()
  }

  if (
    (isLoadingEnterprise ||
      isLoadingShop ||
      isLoadingItems ||
      isErrorEnterprise ||
      isErrorShop ||
      isErrorItems) &&
    (!items || !items.length)
  ) {
    return (
      <Fragment>
        <LayoutLoadingAuthRequired
          isLoading={isLoadingEnterprise || isLoadingShop || isLoadingItems}
          isError={isErrorEnterprise || isErrorShop || isErrorItems}
        />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <LayoutMain title={Title}>
        <HeaderSelectTop>
          <Link
            href={`/e/${enterpriseId}/s/${shopId}/${shopSalePointId}`}
            className="inline-flex items-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground shadow-xs transition-colors hover:bg-muted focus:ring-2 focus:ring-ring focus:outline-none"
          >
            <ArrowLeft
              className="mr-2 size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <span>Назад</span>
          </Link>
        </HeaderSelectTop>

        <RevisionHeader
          searchText={searchText}
          onChangeSearchText={(searchText) => addItemToViewed({ searchText })}
          cancelRevision={cancelRevision}
          isDisabledCancelRevisionButton={!viewedItems?.length}
          finishRevision={finishRevision}
          isDisabledFinishRevisionButton={!viewedItems?.length}
        />

        <div className="grid grid-cols-2 gap-x-8 px-8 pb-8">
          <RevisionGridCol
            prefixTitle={"Количество товара для ревизии"}
            items={revisionItems}
            countAllItems={countRevisionItems}
            onClickCol={(item) => addItemToViewed({ searchText: item.barcode })}
            limit={limitRevisionItems}
            offset={offsetRevisionItems}
            onClickPrev={() =>
              setLimitOffsetRevisionItems(
                limitRevisionItems,
                offsetRevisionItems - limitRevisionItems
              )
            }
            onClickNext={() =>
              setLimitOffsetRevisionItems(
                limitRevisionItems,
                offsetRevisionItems + limitRevisionItems
              )
            }
          />

          <RevisionGridCol
            prefixTitle={"Количество проверенного товара"}
            items={viewedItems}
            countAllItems={countViewedItems}
            onClickCol={(item) =>
              removeItemFromViewed({ searchText: item.barcode })
            }
            limit={limitViewedItems}
            offset={offsetViewedItems}
            onClickPrev={() =>
              setLimitOffsetViewedItems(
                limitViewedItems,
                offsetViewedItems - limitViewedItems
              )
            }
            onClickNext={() =>
              setLimitOffsetViewedItems(
                limitViewedItems,
                offsetViewedItems + limitViewedItems
              )
            }
          />
        </div>

        <RevisionAudio
          audioFailedRef={audioFailedRef}
          audioSuccessRef={audioSuccessRef}
        />
      </LayoutMain>
    </Fragment>
  )
}

export default RevisionPage
